import { Button, Stack, Typography, SxProps, Theme } from "@mui/material";
import { Controller, Control, FieldPath, FieldValues } from "react-hook-form";

interface Option {
  value: any;
  label: string;
}

interface FormButtonGroupProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  options: Option[];
  label?: string;
  direction?: "row" | "column";
  gap?: number;
  buttonSx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
  required?: boolean;
}

export const FormButtonGroup = <T extends FieldValues>({
  name,
  control,
  options,
  label,
  direction = "row",
  gap = 2,
  buttonSx,
  containerSx,
  required = false,
}: FormButtonGroupProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Stack spacing={2} sx={containerSx} data-field={name}>
          {label && (
            <Typography
              fontWeight="bold"
              variant="body1"
              color={error ? "error" : "inherit"}
            >
              {label} {required && "*"}
            </Typography>
          )}
          <Stack direction={direction} gap={gap}>
            {options.map((option) => (
              <Button
                key={String(option.value)}
                variant={
                  field.value === option.value ? "contained" : "outlined"
                }
                onClick={() => field.onChange(option.value)}
                sx={{
                  color: "black",
                  boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                  ...buttonSx,
                }}
              >
                {option.label}
              </Button>
            ))}
          </Stack>
          {error && (
            <Typography color="error" variant="caption">
              {error.message}
            </Typography>
          )}
        </Stack>
      )}
    />
  );
};
